import {useState, CSSProperties} from 'react'
import {Link} from 'react-router-dom'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import {registerRenter} from '../core/_requests'
import {useAuth} from '../core/Auth'
import {ApiError} from '../../../services/api'

const registrationSchema = Yup.object().shape({
  firstName: Yup.string().min(1).max(60).required('First name is required'),
  lastName: Yup.string().min(1).max(60).required('Last name is required'),
  email: Yup.string().email('Wrong email format').max(50).required('Email is required'),
  password: Yup.string().min(8, 'Minimum 8 characters').max(72).required('Password is required'),
})

const initialValues = {firstName: '', lastName: '', email: '', password: ''}

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: 14,
  fontWeight: 600,
  color: '#374151',
  marginBottom: 8,
}

const inputStyle = (invalid?: boolean): CSSProperties => ({
  width: '100%',
  padding: '12px 14px',
  border: `1px solid ${invalid ? '#f87171' : '#e5e7eb'}`,
  borderRadius: 8,
  fontSize: 14,
  color: '#1e293b',
  background: '#f9fafb',
  outline: 'none',
  boxSizing: 'border-box',
})

const errStyle: CSSProperties = {color: '#dc2626', fontSize: 12, margin: '4px 0 0'}

/**
 * Renter self-registration. Creates a RENTER account and signs them straight
 * in, so they land on the renter home. (Admins are provisioned separately.)
 */
export function Registration() {
  const [loading, setLoading] = useState(false)
  const {saveAuth, setCurrentUser} = useAuth()

  const formik = useFormik({
    initialValues,
    validationSchema: registrationSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        const {data: auth, user} = await registerRenter(values)
        saveAuth(auth)
        setCurrentUser(user)
      } catch (error) {
        saveAuth(undefined)
        const msg =
          error instanceof ApiError && error.status === 409
            ? 'An account with this email already exists'
            : 'Registration failed. Please try again.'
        setStatus(msg)
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  return (
    <div
      style={{
        background: '#ffffff',
        borderRadius: 16,
        padding: 'clamp(26px, 7vw, 44px) clamp(20px, 6vw, 40px)',
        width: '100%',
        maxWidth: 560,
        boxShadow: '0 14px 44px rgba(27,27,41,0.1)',
        border: '1px solid #eef0f4',
      }}
    >
      <div style={{textAlign: 'center', marginBottom: 28}}>
        <img
          src='/app-icon.png'
          alt='Crew Crashpad'
          style={{width: 64, height: 64, borderRadius: 14, marginBottom: 16, objectFit: 'cover', boxShadow: '0 8px 20px rgba(0,0,0,0.15)'}}
        />
        <h1 style={{fontSize: 24, fontWeight: 700, color: '#1e293b', margin: '0 0 6px'}}>Create your crew account</h1>
        <p style={{fontSize: 14, color: '#64748b', margin: 0}}>Register as a crashpad renter</p>
      </div>

      {formik.status && (
        <div style={{background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', marginBottom: 20, color: '#dc2626', fontSize: 14}}>
          {formik.status}
        </div>
      )}

      <form onSubmit={formik.handleSubmit} noValidate>
        <div style={{display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 18}}>
          <div style={{flex: '1 1 200px'}}>
            <label style={labelStyle}>First name</label>
            <input style={inputStyle(!!(formik.touched.firstName && formik.errors.firstName))} {...formik.getFieldProps('firstName')} />
            {formik.touched.firstName && formik.errors.firstName && <p style={errStyle}>{formik.errors.firstName}</p>}
          </div>
          <div style={{flex: '1 1 200px'}}>
            <label style={labelStyle}>Last name</label>
            <input style={inputStyle(!!(formik.touched.lastName && formik.errors.lastName))} {...formik.getFieldProps('lastName')} />
            {formik.touched.lastName && formik.errors.lastName && <p style={errStyle}>{formik.errors.lastName}</p>}
          </div>
        </div>

        <div style={{marginBottom: 18}}>
          <label style={labelStyle}>Email Address</label>
          <input type='email' autoComplete='off' style={inputStyle(!!(formik.touched.email && formik.errors.email))} {...formik.getFieldProps('email')} />
          {formik.touched.email && formik.errors.email && <p style={errStyle}>{formik.errors.email}</p>}
        </div>

        <div style={{marginBottom: 24}}>
          <label style={labelStyle}>Password</label>
          <input type='password' autoComplete='off' placeholder='At least 8 characters' style={inputStyle(!!(formik.touched.password && formik.errors.password))} {...formik.getFieldProps('password')} />
          {formik.touched.password && formik.errors.password && <p style={errStyle}>{formik.errors.password}</p>}
        </div>

        <button
          type='submit'
          disabled={formik.isSubmitting || !formik.isValid}
          style={{
            width: '100%',
            padding: 13,
            background: formik.isSubmitting || !formik.isValid ? '#7ec79a' : 'linear-gradient(135deg, #039540, #027a34)',
            color: '#ffffff',
            border: 'none',
            borderRadius: 8,
            fontSize: 15,
            fontWeight: 600,
            cursor: formik.isSubmitting || !formik.isValid ? 'not-allowed' : 'pointer',
            letterSpacing: '0.3px',
          }}
        >
          {loading ? 'Please wait…' : 'Create account'}
        </button>
      </form>

      <p style={{textAlign: 'center', fontSize: 14, color: '#64748b', margin: '22px 0 0'}}>
        Already have an account?{' '}
        <Link to='/auth/login' style={{color: '#039540', fontWeight: 600, textDecoration: 'none'}}>
          Sign in
        </Link>
      </p>
    </div>
  )
}
